import {
  useCellPeriod,
  useUpdateCellPeriod,
} from "@/components/modals/mapping-info/hooks";
import { useWorkbookId } from "@/hooks/use-workbook";
import { cn } from "@/lib/utils";
import { CellType, useMappingStore } from "@/stores/mapping";
import { Popover, PopoverAnchor, PopoverTrigger } from "@/styles/ui/popover";
import { Skeleton } from "@/styles/ui/skeleton";
import { Hash, Heading, Sigma } from "lucide-react";
import { useMemo, useState } from "react";
import { useMappingDetailValue } from "../detail/hooks";
import { useSelectedTable } from "../hooks";
import { CellAddressBadge } from "./cell-address-badge";
import { CellPeriodChips } from "./cell-meta-chips";
import {
  MappingCellPropertyPopoverContent,
  MappingCellPropertyPopoverTrigger,
} from "./cell-property-popover";

const stripHtml = (html: string): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

export const MappingCellDataSection = () => {
  // 셀 주소
  const selectedFact = useMappingStore((state) => state.fact.selected);

  const cellValue = useMappingDetailValue();

  // Strip HTML from the cell value
  const value = useMemo(() => {
    return stripHtml(cellValue?.toString() || "");
  }, [cellValue]);

  const { data: workbookId } = useWorkbookId();
  const {
    data: { tableId },
  } = useSelectedTable();
  const selectedFactAddress = selectedFact?.address ?? null;

  const { cellTags, isFetching, isLoading } = useCellPeriod({
    workbookId,
    tableId,
    selectedFact,
  });
  const { isPuttingPeriod } = useUpdateCellPeriod({
    workbookId,
    tableId,
    selectedFact,
  });

  const targetYear = cellTags?.targetYear ?? null; // 당기/전기
  const accountingPeriod = cellTags?.period ?? null; // 기초/기말
  const accumulated = cellTags?.accumulated ?? null; // 누적

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const cellTypeIcon = {
    [CellType.HEADER]: <Heading className="w-3.5 h-3.5" />,
    [CellType.DATA]: <Hash className="w-3.5 h-3.5" />,
    [CellType.SUM]: <Sigma className="w-3.5 h-3.5" />,
  };

  return (
    <Popover onOpenChange={setIsPopoverOpen}>
      <div className="relative flex flex-row w-full items-center gap-[12px] py-[12px] px-[16px] border-b-[1px] border-neutral-200">
        <PopoverAnchor className="popover-anchor h-full absolute -left-[0px]" />
        {/* Frame 2087329532 */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Frame 2087329487 */}
          {isLoading ? (
            <Skeleton className="h-[36px] w-[36px] rounded-lg bg-neutral-100" />
          ) : (
            <CellAddressBadge
              className={cn(
                "transition-opacity duration-300 delay-300",
                (isFetching || isPuttingPeriod) && "opacity-50",
              )}
            >
              {selectedFact?.cellType
                ? cellTypeIcon[
                    selectedFact?.cellType as keyof typeof cellTypeIcon
                  ]
                : "-"}
            </CellAddressBadge>
          )}
          {/* Frame 2087329486 */}
          <div className="flex flex-col justify-center items-start min-w-0 h-12 gap-0.5">
            {value && !isLoading ? (
              <div
                className={cn(
                  "w-full overflow-hidden text-ellipsis whitespace-nowrap transition-opacity duration-300 delay-300",
                  (isFetching || isPuttingPeriod) && "opacity-50",
                )}
                key={selectedFactAddress}
              >
                <span className="truncate leading-[146%] text-primary body-2-normal-medium">
                  {value}
                </span>
              </div>
            ) : (
              <Skeleton
                key={selectedFactAddress}
                className="h-4.5 w-full rounded-lg bg-neutral-100"
              />
            )}
            {/* Frame 2087329489 */}
            {isLoading ? (
              <Skeleton className="h-3 w-25 rounded-lg bg-neutral-100 mt-1" />
            ) : (
              <CellPeriodChips
                className={cn(
                  "transition-opacity duration-300 delay-300 truncate leading-[138%]",
                  (isFetching || isPuttingPeriod) && "opacity-50",
                )}
                targetYear={targetYear}
                accountingPeriod={accountingPeriod}
                accumulated={accumulated}
              />
            )}
          </div>
        </div>
        {/* Button */}
        <PopoverTrigger disabled={isFetching || isPuttingPeriod}>
          {isFetching || isPuttingPeriod ? (
            <Skeleton className="h-[28px] w-[28px] rounded-lg bg-neutral-100" />
          ) : (
            <MappingCellPropertyPopoverTrigger isOpen={isPopoverOpen} />
          )}
        </PopoverTrigger>
      </div>
      <MappingCellPropertyPopoverContent />
    </Popover>
  );
};
